import {AxiosError} from 'axios';
import {useCallback, useEffect, useState} from 'react';
import {PaginatedArrayResponse} from '../repositories/interfaces/paginated-array-response';
import {API_PAGINATION_MAX_LIMIT} from '../utils/constants/api';

interface PaginatedFetchArgs {
  limit: number;
  skip: number;
}

// Mimics the DependencyList on react hooks, as the original type is not exported
type DependencyList = ReadonlyArray<unknown>;

export const useFetchAllItems = <T>({
  fetchFunction,
  enabled,
  deps,
  transform,
}: {
  fetchFunction: (args: PaginatedFetchArgs) => Promise<PaginatedArrayResponse<T>>;
  enabled?: boolean;
  deps?: DependencyList;
  /** Transform data after fetching all items */
  transform?: (items: T[]) => T[] | Promise<T[]>;
}) => {
  const [items, setItems] = useState<T[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<AxiosError | undefined>(undefined);

  const fetchAllItems = useCallback(async () => {
    if (enabled === undefined || enabled) {
      // Start loading
      setIsLoading(true);

      const allItems = [];
      let totalCount;

      // Keep fetching until we have the total count of items
      do {
        try {
          const response: PaginatedArrayResponse<T> = await fetchFunction({
            limit: API_PAGINATION_MAX_LIMIT,
            skip: allItems.length,
          });
          totalCount ||= response.totalCount;
          allItems.push(...response.data);
        } catch (error: unknown) {
          // Throw any error that's not related with the network layer, as we're not expecting that to happen.
          if (!(error instanceof AxiosError)) {
            throw error;
          }

          setError(error);
        }
      } while (!error && totalCount && allItems.length < totalCount);

      // Transform data if transform function is provided
      let transformedItems = allItems;
      if (transform && !error) {
        transformedItems = await transform(allItems);
      }

      // Hold state
      setItems(transformedItems);

      // Stop loading
      setIsLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...(deps ?? [])]);

  useEffect(() => {
    fetchAllItems();
  }, [fetchAllItems]);

  const refreshItems = (clearExistingItems: boolean = false) => {
    if (clearExistingItems) {
      setItems(undefined);
    }

    fetchAllItems();
  };

  return {
    items,
    isLoading,
    error,
    refreshItems,
  };
};

export const useFetchCompleteList = <T>({
  fetchFunction,
  enabled,
  deps,
  transform,
}: {
  fetchFunction: () => Promise<Array<T>>;
  enabled?: boolean;
  deps?: DependencyList;
  /** Transform data after fetching all items */
  transform?: (items: T[]) => T[] | Promise<T[]>;
}) => {
  const [items, setItems] = useState<T[] | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<AxiosError | undefined>(undefined);

  const fetchAllItems = useCallback(async () => {
    if (enabled === undefined || enabled) {
      // Start loading
      setIsLoading(true);

      try {
        const response: Array<T> = await fetchFunction();

        // Transform data if transform function is provided
        let transformedItems = response;
        if (transform) {
          transformedItems = await transform(response);
        }

        setItems(transformedItems);
      } catch (error: unknown) {
        // Throw any error that's not related with the network layer, as we're not expecting that to happen.
        if (!(error instanceof AxiosError)) {
          throw error;
        }

        setError(error);
      }

      // Stop loading
      setIsLoading(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...(deps ?? [])]);

  useEffect(() => {
    fetchAllItems();
  }, [fetchAllItems]);

  return {
    items,
    isLoading,
    error,
  };
};
